import React, { useState } from 'react'
import moment from 'moment'
import 'moment/locale/de'
moment.locale('de')

/**
 * Hook used to expand a table row or collapse if already expanded. Used for Collapse Components inside of Tables.
 * Returns the expandRow mechanism as well as a function to check whether a row is expanded or not
*/
const useExpandRows = () => {
    const [expandedRows, setExpandedRows] = useState([])

    /**
     * Sets a row as expanded or removes it from the array if already expanded
     * @param {any} e default click event
     * @param {number} index unique identifier to set a row as expanded or remove it
     */
    const expandRow = (e, index) => {
        e.preventDefault()
        console.log("EXPANDS ROW")
        const expandedRowsCopy = [...expandedRows]
        expandedRowsCopy.includes(index) ?
            expandedRowsCopy.splice(expandedRowsCopy.indexOf(index), 1) :
                expandedRowsCopy.push(index)
        setExpandedRows(expandedRowsCopy)
        console.log("EXPANDS ROW2")
    }

    /**
     * Gets a bool whether a rows identifier is already set as expanded
     * @param {number} index unique identifier to determine if a row is expanded
     */
    const getIsExpandedRow = (index) => {
        console.log("GET IS EXPANDS ROW2")
        return (expandedRows.includes(index))
    }

    return { getIsExpandedRow, expandRow }
}

/**
 * Custom hook to sort an array of transactions 
 * @param {object[]} transactions array of objects containing transaction data
 * @param {any} setFilteredTransactions react hook to set the transactions of parent component as sorted array
 */
const useSortTransactions = (transactions, setFilteredTransactions) => {
    const [sortTransactionOrderAsc, setSortTransactionOrderAsc] = useState(true)
    const [sortTransactionField, setSortTransactionField] = useState("")

    /**
     * Sorts the transactions after a given field. 
     * The first sorting order is ascending, sorting for the same field again changes the order to descending.
     * Uses a temporary sorting value, since the state set via useState hooks might not be available immediately
     * @param {string} field field to be sorted after
     */
    const sortTransactions = (e, field) => {
        e.preventDefault()
        let sortOrderAscTemp = sortTransactionOrderAsc

        if (sortTransactionField === field) {
            setSortTransactionOrderAsc(!sortTransactionOrderAsc)
            sortOrderAscTemp = (!sortTransactionOrderAsc)
        } else {
            setSortTransactionField(field)
            setSortTransactionOrderAsc(true)
            sortOrderAscTemp = true
        }

        if (field === "CreationDate") {
            const transactionCopyForDate =
                [...transactions].sort((a, b) =>
                    sortOrderAscTemp === true ? (moment(moment(a.CreationDate, "DD.MM.YYYY").format("YYYYMMDD"))) - (moment(moment(b.CreationDate, "DD.MM.YYYY").format("YYYYMMDD")))
                        : (moment(moment(b.CreationDate, "DD.MM.YYYY").format("YYYYMMDD"))) - (moment(moment(a.CreationDate, "DD.MM.YYYY").format("YYYYMMDD"))))

            setFilteredTransactions(transactionCopyForDate)
        } else if (field === "TransactionDate") {
            const transactionCopyForDate =
                [...transactions].sort((a, b) =>
                    sortOrderAscTemp === true ? (moment(moment((a.TransactionDate !== "" ? a.TransactionDate : "01.01.0001"), "DD.MM.YYYY").format("YYYYMMDD"))) - (moment(moment((b.TransactionDate !== "" ? b.TransactionDate : "01.01.0001"), "DD.MM.YYYY").format("YYYYMMDD")))
                        : (moment(moment((b.TransactionDate !== "" ? b.TransactionDate : "01.01.0001"), "DD.MM.YYYY").format("YYYYMMDD"))) - (moment(moment((a.TransactionDate !== "" ? a.TransactionDate : "01.01.0001"), "DD.MM.YYYY").format("YYYYMMDD"))))

            setFilteredTransactions(transactionCopyForDate)
        } else if (field === "Date") {
            const transactionCopyForDate =
                [...transactions].sort((a, b) =>
                    sortOrderAscTemp === true ? (moment(moment(a.Date, "DD.MM.YYYY").format("YYYYMMDD"))) - (moment(moment(b.Date, "DD.MM.YYYY").format("YYYYMMDD")))
                        : (moment(moment(b.Date, "DD.MM.YYYY").format("YYYYMMDD"))) - (moment(moment(a.Date, "DD.MM.YYYY").format("YYYYMMDD"))))

            setFilteredTransactions(transactionCopyForDate)
        } else {
            const transactionCopy =
                [...transactions].sort((a, b) => {
                    if (a[field].toString().toLowerCase() < b[field].toString().toLowerCase()) {
                        return sortOrderAscTemp === true ? -1 : 1;
                    }
                    if (a[field].toString().toLowerCase() > b[field].toString().toLowerCase()) {
                        return sortOrderAscTemp === true ? 1 : -1;
                    }

                    return 0;
                })
            setFilteredTransactions(transactionCopy)
        }
    }

    return { sortTransactions }
}

/**
 * Custom hook to sort an array of articleTypes 
 * @param {object[]} articleTypes array of objects containing articleType data
 * @param {any} setFilteredArticleTypes react hook to set the articleType of parent component as sorted array
 */
const useSortArticleTypes = (articleTypes, setFilteredArticleTypes) => {
    const [sortArticleTypeOrderAsc, setSortArticleTypeOrderAsc] = useState(true)
    const [sortArticleTypeField, setSortArticleTypeField] = useState("")

    /**
     * Sorts the articleTypes after a given field. 
     * The first sorting order is ascending, sorting for the same field again changes the order to descending.
     * Uses a temporary sorting value, since the state set via useState hooks might not be available immediately
     * @param {string} field field to be sorted after
     */
    const sortArticleTypes = (field) => {
        let sortOrderAscTemp = sortArticleTypeOrderAsc

        if (sortArticleTypeField === field) {
            setSortArticleTypeOrderAsc(!sortArticleTypeOrderAsc)
            sortOrderAscTemp = (!sortArticleTypeOrderAsc)
        } else {
            setSortArticleTypeField(field)
            setSortArticleTypeOrderAsc(true)
            sortOrderAscTemp = true
        }

        const articleTypesCopy =
            [...articleTypes].sort((a, b) => {
                if (a[field] < b[field]) {
                    return sortOrderAscTemp === true ? -1 : 1;
                }
                if (a[field] > b[field]) {
                    return sortOrderAscTemp === true ? 1 : -1;
                }

                return 0;
            })
        setFilteredArticleTypes(articleTypesCopy)
    }

    return { sortArticleTypes }
}

/**
 * Custom hook to sort an array of articles
 * @param {object[]} stock array of articles defining the stock
 * @param {any} setFilteredStock useState hook that sets the passed stock with filtered values
 */
const useSortStock = (stock, setFilteredStock) => {
    const [sortArticlesOrderAsc, setSortArticlesOrderAsc] = useState(true)
    const [sortArticlesField, setSortArticlesField] = useState("")

    /**
     * Sorts the stock after a given field.
     * The first sorting order is ascending, sorting for the same field again changes the order to descending.
     * Uses a temporary sorting value, since the state set via useState hooks might not be available immediately
     * @param {string} field field to be sorted after
     */
    const sortArticlesViaStock = (e, field) => {
        e.preventDefault()

        let sortOrderAscTemp = sortArticlesOrderAsc

        if (sortArticlesField === field) {
            setSortArticlesOrderAsc(!sortArticlesOrderAsc)
            sortOrderAscTemp = (!sortArticlesOrderAsc)
        } else {
            setSortArticlesField(field)
            setSortArticlesOrderAsc(true)
            sortOrderAscTemp = true
        }
        const articlesCopy = [...stock]

        sortOrderAscTemp ? articlesCopy.sort(function (a, b) { return a.ArticleStock[field] - b.ArticleStock[field]; })
            : articlesCopy.sort(function (a, b) { return b.ArticleStock[field] - a.ArticleStock[field]; })

        setFilteredStock(articlesCopy)
    }

    /**
     * Sorts the articles after a given field.
     * The first sorting order is ascending, sorting for the same field again changes the order to descending.
     * Uses a temporary sorting value, since the state set via useState hooks might not be available immediately
     * @param {string} field field to be sorted after
     */
    const sortArticles = (e, field) => {
        e.preventDefault()

        let sortOrderAscTemp = sortArticlesOrderAsc

        if (sortArticlesField === field) {
            setSortArticlesOrderAsc(!sortArticlesOrderAsc)
            sortOrderAscTemp = (!sortArticlesOrderAsc)
        } else {
            setSortArticlesField(field)
            setSortArticlesOrderAsc(true)
            sortOrderAscTemp = true
        }
        const articlesCopy = [...stock]

        if (field === "SalePrice") {
            sortOrderAscTemp ? articlesCopy.sort(function (a, b) { return a[field] - b[field]; })
                : articlesCopy.sort(function (a, b) { return b[field] - a[field]; })
        } else {
            articlesCopy.sort((a, b) => {
                if (a[field].trim() < b[field].trim()) {
                    return sortOrderAscTemp === true ? -1 : 1;
                }
                if (a[field].trim() > b[field].trim()) {
                    return sortOrderAscTemp === true ? 1 : -1;
                }

                return 0;
            })
        }
        setFilteredStock(articlesCopy)
    }

    return { sortArticlesViaStock, sortArticles }
}

const useSortSalesReport = (report, setFilteredReport) => {
    const [sortReportOrderAsc, setSortReportOrderAsc] = useState(true)
    const [sortReportField, setSortReportField] = useState("")

    const sortSalesReport = (e, field) => {
        e.preventDefault()

        let sortOrderAscTemp = sortReportOrderAsc

        if (sortReportField === field) {
            setSortReportOrderAsc(!sortReportOrderAsc)
            sortOrderAscTemp = (!sortReportOrderAsc)
        } else {
            setSortReportField(field)
            setSortReportOrderAsc(true)
            sortOrderAscTemp = true
        }

        const reportCopy = [...report]

        console.log("tableHooks ", field, reportCopy, reportCopy.map(a => moment(moment(a.ClearanceDate !== "" && a.ClearanceDate !== undefined && a.ClearanceDate !== null ? a.ClearanceDate : "01.01.0001", "DD.MM.YYYY").format("YYYYMMDD")) - moment(moment(reportCopy[0].ClearanceDate, "DD.MM.YYYY").format("YYYYMMDD"))))

        if (field === "Date") {
            reportCopy.sort((a, b) =>
                sortOrderAscTemp === true ? (moment(moment(a.Date, "DD.MM.YYYY").format("YYYYMMDD"))) - (moment(moment(b.Date, "DD.MM.YYYY").format("YYYYMMDD")))
                    : (moment(moment(b.Date, "DD.MM.YYYY").format("YYYYMMDD"))) - (moment(moment(a.Date, "DD.MM.YYYY").format("YYYYMMDD"))))
        } else if (field === "ClearanceDate") {
            reportCopy.sort((a, b) =>
                sortOrderAscTemp === true ? (moment(moment(a.ClearanceDate !== '' && a.ClearanceDate !== undefined && a.ClearanceDate !== null ? a.ClearanceDate : "01.01.0001", "DD.MM.YYYY").format("YYYYMMDD"))) - (moment(moment(b.ClearanceDate !== '' && b.ClearanceDate !== undefined && b.ClearanceDate !== null? b.ClearanceDate : "01.01.0001", "DD.MM.YYYY").format("YYYYMMDD")))
                    : (moment(moment(b.ClearanceDate !== '' && b.ClearanceDate !== undefined && b.ClearanceDate !== null ? b.ClearanceDate : "01.01.0001", "DD.MM.YYYY").format("YYYYMMDD"))) - (moment(moment(a.ClearanceDate !== '' && a.ClearanceDate !== undefined && a.ClearanceDate !== null? a.ClearanceDate : "01.01.0001", "DD.MM.YYYY").format("YYYYMMDD"))))
        }
        else {
            reportCopy.sort((a, b) => {
                let aField = typeof (a[field]) === "string" ? a[field].toString().toLowerCase() : a[field]
                let bField = typeof (b[field]) === "string" ? b[field].toString().toLowerCase() : b[field]
                if (aField < bField) {
                    return sortOrderAscTemp === true ? -1 : 1;
                }
                if (aField > bField) {
                    return sortOrderAscTemp === true ? 1 : -1;
                }

                return 0;
            })
        }
        setFilteredReport(reportCopy)
    }
    return { sortSalesReport }
}

export { useExpandRows, useSortTransactions, useSortArticleTypes, useSortStock, useSortSalesReport }